import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { CheckCircle, Eye, Mail01, X, AlertCircle } from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { RadioButtonBase } from '@/components/base/radio-buttons/radio-buttons';
import type { QuoteResponse, ShipperQuoteRequest } from '@/types/quote';
import { useTranslation } from 'react-i18next';

interface QuoteResponsesListProps {
    quoteRequest: ShipperQuoteRequest;
    onSelectQuote: (quoteResponseId: string) => void;
    onComplete: () => void;
    onViewDetails?: (quote: QuoteResponse) => void;
    onAccept?: (quoteId: string) => void;
    onReject?: (quoteId: string) => void;
    onRequestModification?: (quoteId: string, body: string, subject?: string) => void;
}

export const QuoteResponsesList = ({ quoteRequest, onSelectQuote, onComplete, onViewDetails, onAccept, onReject, onRequestModification }: QuoteResponsesListProps) => {
    const { t } = useTranslation(['pages:quotes', 'common']);
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(quoteRequest.selectedQuoteId || null);

    const handleSelectQuote = (quoteId: string) => {
        setSelectedQuoteId(quoteId);
        onSelectQuote(quoteId);
    };

    const handleComplete = () => {
        if (selectedQuoteId) {
            onComplete();
        }
    };

    const getQuoteResponseStatusBadge = (status?: string) => {
        if (!status || status === 'pending') return null;

        switch (status) {
            case 'accepted':
                return (
                    <Badge color="success" size="sm">
                        {t('shipper.responses.accepted') || 'Accettata'}
                    </Badge>
                );
            case 'rejected':
                return (
                    <Badge color="error" size="sm">
                        {t('shipper.responses.rejected') || 'Rifiutata'}
                    </Badge>
                );
            case 'modification_requested':
                return (
                    <Badge color="warning" size="sm">
                        {t('shipper.responses.modificationRequested') || 'Modifica Richiesta'}
                    </Badge>
                );
            default:
                return null;
        }
    };

    const isActionDisabled = quoteRequest.status === 'completed' || quoteRequest.status === 'cancelled';

    if (quoteRequest.receivedQuotes.length === 0) {
        return (
            <div className="rounded-xl bg-primary p-8 shadow-xs ring-1 ring-secondary text-center">
                <Mail01 className="mx-auto h-12 w-12 text-tertiary mb-4" />
                <p className="text-tertiary">{t('shipper.responses.noResponses') || 'Nessun preventivo ricevuto ancora'}</p>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-4">
                {quoteRequest.receivedQuotes.map((quote) => (
                    <div
                        key={quote.id}
                        className={`rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary cursor-pointer transition-all ${selectedQuoteId === quote.id ? 'ring-2 ring-brand-600' : ''
                            } ${quoteRequest.status === 'completed' && quoteRequest.selectedQuoteId === quote.id ? 'bg-success-50/10' : ''}`}
                        onClick={() => {
                            if (quoteRequest.status !== 'completed') {
                                if (id) {
                                    navigate(`/shipper/quotes/${id}/response/${quote.id}`);
                                } else {
                                    handleSelectQuote(quote.id);
                                }
                            }
                        }}
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-3">
                                    <RadioButtonBase
                                        isSelected={selectedQuoteId === quote.id}
                                        isDisabled={quoteRequest.status === 'completed'}
                                    />
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <h3 className="font-semibold text-primary">{quote.fromName || quote.from}</h3>
                                            {quoteRequest.status === 'completed' && quoteRequest.selectedQuoteId === quote.id && (
                                                <Badge color="success" size="sm">
                                                    {t('shipper.responses.selected') || 'Selezionato'}
                                                </Badge>
                                            )}
                                            {getQuoteResponseStatusBadge(quote.status)}
                                        </div>
                                        <p className="text-sm text-tertiary">{quote.from}</p>
                                    </div>
                                </div>

                                <div className="ml-8 space-y-2">
                                    <div>
                                        <p className="text-sm font-medium text-primary mb-1">{quote.subject}</p>
                                        <p className="text-xs text-tertiary">
                                            {t('shipper.responses.receivedAt') || 'Ricevuto il'}: {quote.receivedAt.toLocaleDateString('it-IT', {
                                                day: '2-digit',
                                                month: 'long',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                            })}
                                        </p>
                                    </div>

                                    <div className="flex items-center gap-4">
                                        <div>
                                            <span className="text-xs text-tertiary">{t('shipper.responses.price') || 'Prezzo'}: </span>
                                            <span className="text-sm font-semibold text-primary">
                                                {quote.price.toLocaleString('it-IT')} {quote.currency}
                                            </span>
                                        </div>
                                        {quote.validity && (
                                            <div>
                                                <span className="text-xs text-tertiary">{t('shipper.responses.validity') || 'Validità'}: </span>
                                                <span className="text-sm text-primary">{quote.validity} {t('shipper.responses.days') || 'giorni'}</span>
                                            </div>
                                        )}
                                    </div>

                                    {quote.extractedData && (
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {quote.extractedData.product && (
                                                <span className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-xs text-tertiary">
                                                    {t('extracted.product')}: {quote.extractedData.product}
                                                </span>
                                            )}
                                            {quote.extractedData.origin && (
                                                <span className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-xs text-tertiary">
                                                    {t('extracted.origin')}: {quote.extractedData.origin}
                                                </span>
                                            )}
                                            {quote.extractedData.destination && (
                                                <span className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-xs text-tertiary">
                                                    {t('extracted.destination')}: {quote.extractedData.destination}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col gap-2">
                                <Button
                                    type="button"
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                        e.stopPropagation();
                                        if (id) {
                                            navigate(`/shipper/quotes/${id}/response/${quote.id}`);
                                        } else {
                                            onViewDetails?.(quote);
                                        }
                                    }}
                                    color="secondary"
                                    size="sm"
                                    iconLeading={Eye}
                                >
                                    {t('shipper.responses.viewDetails') || 'Dettagli'}
                                </Button>
                                {quote.status === 'pending' && !isActionDisabled && (
                                    <>
                                        <Button
                                            type="button"
                                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                                e.stopPropagation();
                                                onAccept?.(quote.id);
                                            }}
                                            color="primary"
                                            size="sm"
                                            iconLeading={CheckCircle}
                                        >
                                            {t('shipper.responses.accept') || 'Accetta'}
                                        </Button>
                                        <Button
                                            type="button"
                                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                                e.stopPropagation();
                                                onReject?.(quote.id);
                                            }}
                                            color="primary-destructive"
                                            size="sm"
                                            iconLeading={X}
                                        >
                                            {t('shipper.responses.reject') || 'Rifiuta'}
                                        </Button>
                                        <Button
                                            type="button"
                                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                                e.stopPropagation();
                                                // Passa undefined come body per indicare che dobbiamo aprire l'editor
                                                onRequestModification?.(quote.id, '');
                                            }}
                                            color="secondary"
                                            size="sm"
                                            iconLeading={AlertCircle}
                                        >
                                            {t('shipper.responses.requestModification') || 'Richiedi Modifica'}
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {quoteRequest.status !== 'completed' && selectedQuoteId && (
                <div className="mt-6 flex justify-end">
                    <Button
                        onClick={handleComplete}
                        color="primary"
                        size="md"
                        iconLeading={CheckCircle}
                    >
                        {t('shipper.responses.complete') || 'Imposta come Completato'}
                    </Button>
                </div>
            )}
        </>
    );
};

